import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

interface ResetResponse {
  ok: true;
  clienteId: string | null;
  apagados: {
    mensagens: number;
    cartas: number;
    tickets: number;
    eventos: number;
    compras: number;
    mapas: number;
  };
}

interface TestModeConfig {
  enabled: boolean;
  allowedPhones: string[];
  simulatePostPaywallWindow: boolean;
  simulateConfirmCode: string;
  disableDelaysInTestMode: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export function TestModePage(): JSX.Element {
  return (
    <section aria-labelledby="testmode-heading" className="space-y-8 max-w-3xl">
      <header>
        <h1 id="testmode-heading" className="h1">
          Modo Teste
        </h1>
        <p className="muted text-sm mt-1">
          Ferramentas pra apresentar a Sara pro time interno e iterar com segurança antes de abrir
          pra clientes.
        </p>
      </header>

      <WhitelistSection />
      <ResetSection />
    </section>
  );
}

function WhitelistSection(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery<TestModeConfig>({
    queryKey: ['test-mode-config'],
    queryFn: () => request<TestModeConfig>('/admin/test/mode'),
  });

  const [enabled, setEnabled] = useState(false);
  const [phones, setPhones] = useState<string[]>([]);
  const [newPhone, setNewPhone] = useState('');
  const [simulatePostPaywall, setSimulatePostPaywall] = useState(false);
  const [confirmCode, setConfirmCode] = useState('LIBERAR-TESTE');
  const [disableDelays, setDisableDelays] = useState(false);
  // Tokens gerados nesta sessão (mantidos só em memória — se recarregar a página
  // perde, mas dá pra gerar de novo). Telefone normalizado → token SE-XXXXXX.
  const [tokens, setTokens] = useState<Record<string, string>>({});

  const issueToken = useMutation({
    mutationFn: (params: { telefone: string; produto: 'front' | 'upsell1' }) =>
      request<{ token: string; clienteId: string; telefone: string; produto: string }>(
        '/admin/test/issue-token',
        {
          method: 'POST',
          body: params,
        },
      ),
    onSuccess: (data, variables) => {
      // Indexa o token pelo telefone EXATO que o user clicou (variables), não
      // pelo retornado pelo backend — esse último vem normalizado (com 55 e o
      // "9" do celular brasileiro) e pode não bater com o `p` do .map() abaixo
      // se a whitelist tem o número numa forma diferente. Bug observado em
      // 2026-04-30 com numero de Bahia adicionado sem o 9.
      setTokens((prev) => ({ ...prev, [variables.telefone]: data.token }));
    },
  });

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setPhones(data.allowedPhones);
      setSimulatePostPaywall(data.simulatePostPaywallWindow);
      setConfirmCode(data.simulateConfirmCode);
      setDisableDelays(data.disableDelaysInTestMode);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (input: {
      enabled: boolean;
      allowedPhones: string[];
      simulatePostPaywallWindow: boolean;
      simulateConfirmCode: string;
      disableDelaysInTestMode: boolean;
    }) => request<TestModeConfig>('/admin/test/mode', { method: 'PUT', body: input }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['test-mode-config'], fresh);
    },
  });

  function handleAddPhone(): void {
    const cleaned = newPhone.replace(/\D/g, '');
    if (cleaned.length < 10) return;
    if (phones.some((p) => p.replace(/\D/g, '') === cleaned)) {
      setNewPhone('');
      return;
    }
    setPhones((prev) => [...prev, cleaned]);
    setNewPhone('');
  }

  function handleRemovePhone(phone: string): void {
    setPhones((prev) => prev.filter((p) => p !== phone));
  }

  function handleSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    save.mutate({
      enabled,
      allowedPhones: phones,
      simulatePostPaywallWindow: simulatePostPaywall,
      simulateConfirmCode: confirmCode.trim() || 'LIBERAR-TESTE',
      disableDelaysInTestMode: disableDelays,
    });
  }

  const isDirty =
    !!data &&
    (data.enabled !== enabled ||
      JSON.stringify(data.allowedPhones) !== JSON.stringify(phones) ||
      data.simulatePostPaywallWindow !== simulatePostPaywall ||
      data.simulateConfirmCode !== confirmCode ||
      data.disableDelaysInTestMode !== disableDelays);

  return (
    <article aria-labelledby="whitelist-heading" className="space-y-4">
      <div>
        <h2 id="whitelist-heading" className="h2">
          Whitelist global
        </h2>
        <p className="muted text-sm mt-1">
          Quando ativo, a Sara <strong>só responde</strong> os números desta lista — outras
          mensagens são silenciosamente ignoradas. Webhooks da Kiwify são processados normalmente
          (compra+token gravados no DB) mas o <strong>email de boas-vindas só dispara</strong> se o
          telefone da compra estiver na whitelist. Útil pra validar payload real sem mandar email
          pra cliente.
        </p>
      </div>

      {isLoading ? (
        <div className="card text-sm muted">Carregando configuração…</div>
      ) : isError ? (
        <div className="card border-sara-danger" role="alert">
          <p className="text-sara-danger text-sm">Erro ao carregar configuração.</p>
        </div>
      ) : (
        <form onSubmit={handleSave} className="card space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-sara-text">Modo de teste ativado</p>
              <p className="text-xs muted">
                {enabled
                  ? 'Sara responde apenas números da whitelist.'
                  : 'Sara responde todo mundo (modo produção normal).'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              className="toggle"
              data-state={enabled ? 'on' : 'off'}
            >
              <span className="toggle-thumb" aria-hidden="true" />
            </button>
          </div>

          <hr className="border-sara-border" />

          <div>
            <label htmlFor="add-phone" className="label">
              Adicionar telefone
            </label>
            <div className="flex gap-2">
              <input
                id="add-phone"
                type="tel"
                inputMode="tel"
                placeholder="11999999999"
                className="input"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPhone();
                  }
                }}
              />
              <button
                type="button"
                className="btn-ghost"
                onClick={handleAddPhone}
                disabled={newPhone.replace(/\D/g, '').length < 10}
              >
                Adicionar
              </button>
            </div>
            <p className="text-xs text-sara-text-subtle mt-1.5">
              Com ou sem DDI 55. Mínimo 10 dígitos.
            </p>
          </div>

          <div>
            <p className="label">
              Números permitidos{' '}
              <span className="text-xs font-normal text-sara-text-subtle">({phones.length})</span>
            </p>
            {phones.length === 0 ? (
              <p className="text-sm text-sara-text-subtle italic">
                Nenhum número na lista. Se ativar agora, a Sara não responderá ninguém.
              </p>
            ) : (
              <ul className="space-y-2" aria-label="Telefones permitidos">
                {phones.map((p) => {
                  const token = tokens[p];
                  const issuingFront =
                    issueToken.isPending &&
                    issueToken.variables?.telefone === p &&
                    issueToken.variables?.produto === 'front';
                  const issuingUpsell =
                    issueToken.isPending &&
                    issueToken.variables?.telefone === p &&
                    issueToken.variables?.produto === 'upsell1';
                  const issuing = issuingFront || issuingUpsell;
                  return (
                    <li
                      key={p}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-sara-border bg-sara-bg p-3"
                    >
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-sm font-medium text-sara-text">{formatPhone(p)}</p>
                        {token ? (
                          <div className="mt-1.5 flex items-center gap-2">
                            <code className="rounded bg-sara-accent/15 text-sara-accent px-2 py-1 text-sm font-mono font-semibold tracking-wider">
                              {token}
                            </code>
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard?.writeText(token);
                              }}
                              className="text-xs text-sara-text-muted hover:text-sara-text underline-offset-2 hover:underline"
                              aria-label="Copiar token"
                            >
                              Copiar
                            </button>
                            <span className="text-xs text-sara-text-subtle">
                              cole no WhatsApp pra liberar a Sara
                            </span>
                          </div>
                        ) : (
                          <p className="text-xs text-sara-text-subtle mt-0.5">
                            Sem token ativo. Gere um pra esse número testar a liberação da Sara.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => issueToken.mutate({ telefone: p, produto: 'front' })}
                          disabled={issuing}
                          className="btn-ghost text-xs"
                          aria-busy={issuingFront}
                          title="Gerar token de front (Semente Energética). Libera só conversa com a Sara."
                        >
                          {issuingFront ? 'Gerando…' : token ? 'Regenerar front' : 'Gerar front'}
                        </button>
                        <button
                          type="button"
                          onClick={() => issueToken.mutate({ telefone: p, produto: 'upsell1' })}
                          disabled={issuing}
                          className="btn-ghost text-xs"
                          aria-busy={issuingUpsell}
                          title="Gerar token de upsell (Mapa Astral da Prosperidade). Libera fluxo de mapa end-to-end."
                        >
                          {issuingUpsell ? 'Gerando…' : 'Gerar upsell'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemovePhone(p)}
                          className="text-sara-text-muted hover:text-sara-danger px-2 leading-none text-lg"
                          aria-label={`Remover ${p}`}
                          title="Remover da whitelist"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {issueToken.isError && (
              <p role="alert" className="text-sara-danger text-xs mt-2">
                {issueToken.error instanceof ApiError
                  ? issueToken.error.message
                  : 'Erro ao gerar token.'}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-sara-border">
            <p className="text-xs text-sara-text-subtle">
              {data?.updatedAt
                ? `Última atualização: ${new Date(data.updatedAt).toLocaleString('pt-BR')}${data.updatedBy ? ` por ${data.updatedBy}` : ''}`
                : null}
            </p>
            <button
              type="submit"
              className="btn-primary"
              disabled={!isDirty || save.isPending}
              aria-busy={save.isPending}
            >
              {save.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>

          {save.isError && (
            <p role="alert" className="text-sara-danger text-sm">
              {save.error instanceof ApiError ? save.error.message : 'Erro ao salvar.'}
            </p>
          )}
          {save.isSuccess && !isDirty && (
            <p role="status" className="text-sara-success text-sm">
              ✓ Configuração salva.
            </p>
          )}

          <hr className="border-sara-border" />

          <div>
            <p className="label">Simulador de paywall (testes da Sara cobrando)</p>
            <p className="text-xs text-sara-text-subtle mb-3">
              Ferramentas pra exercitar o fluxo da "lei da troca" e do pós-pix sem precisar esperar
              7 dias reais nem comprar de verdade na Kiwify. Vale só pros números da whitelist.
            </p>

            <div className="flex items-center justify-between gap-4 rounded-lg border border-sara-border bg-sara-bg p-3">
              <div>
                <p className="text-sm font-medium text-sara-text">Simular janela pós-7 dias</p>
                <p className="text-xs muted">
                  {simulatePostPaywall
                    ? 'Sara trata clientes da whitelist como se a primeira compra fosse há mais de 7 dias — paywall ativo.'
                    : 'Janela de 7 dias funciona normalmente (clientes recém-comprados não veem paywall).'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={simulatePostPaywall}
                onClick={() => setSimulatePostPaywall((v) => !v)}
                className="toggle"
                data-state={simulatePostPaywall ? 'on' : 'off'}
              >
                <span className="toggle-thumb" aria-hidden="true" />
              </button>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-lg border border-sara-border bg-sara-bg p-3 mt-3">
              <div>
                <p className="text-sm font-medium text-sara-text">
                  Desligar delays (debounce + typing + pacing)
                </p>
                <p className="text-xs muted">
                  {disableDelays
                    ? 'Sara responde em ~1s — sem debounce de 2 min, sem pacing entre balões, sem "digitando..." longo. Vale só pra whitelist.'
                    : 'Comportamento humanizado normal: debounce de 2 min, pacing entre balões, "digitando..." proporcional ao tamanho.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={disableDelays}
                onClick={() => setDisableDelays((v) => !v)}
                className="toggle"
                data-state={disableDelays ? 'on' : 'off'}
              >
                <span className="toggle-thumb" aria-hidden="true" />
              </button>
            </div>

            <label htmlFor="confirm-code" className="label mt-4">
              Código pra simular pagamento confirmado
            </label>
            <input
              id="confirm-code"
              type="text"
              className="input font-mono"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.toUpperCase())}
              maxLength={64}
              placeholder="LIBERAR-TESTE"
            />
            <p className="text-xs text-sara-text-subtle mt-1.5">
              Quando o cliente da whitelist enviar exatamente esse código pelo WhatsApp, a Sara
              trata como webhook Kiwify aprovado: cria Compra(tiragem_avulsa, manual:UUID), credita
              +1 crédito, dispara confirmação pós-pix e (se houver pergunta de tiragem pendente)
              executa a tiragem proativamente. Comparação case-insensitive.
            </p>
          </div>
        </form>
      )}
    </article>
  );
}

function ResetSection(): JSX.Element {
  const [telefone, setTelefone] = useState('');
  const [lastResult, setLastResult] = useState<ResetResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: (tel: string) =>
      request<ResetResponse>('/admin/test/reset-cliente', {
        method: 'POST',
        body: { telefone: tel },
      }),
    onSuccess: (data) => {
      setLastResult(data);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'erro ao resetar');
      setLastResult(null);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!telefone.trim()) return;
    reset.mutate(telefone.trim());
  }

  return (
    <article aria-labelledby="reset-heading" className="space-y-4">
      <div>
        <h2 id="reset-heading" className="h2">
          Resetar histórico de cliente
        </h2>
        <p className="muted text-sm mt-1">
          Apaga mensagens, cartas, tickets, eventos, compras e mapa do número informado, mantendo o
          Cliente. <strong>Operação destrutiva.</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-3" aria-label="Resetar cliente">
        <div>
          <label htmlFor="telefone" className="label">
            Telefone (com ou sem DDI 55)
          </label>
          <input
            id="telefone"
            type="tel"
            inputMode="tel"
            placeholder="11999999999"
            className="input"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            required
            minLength={8}
            aria-describedby={error ? 'reset-error' : undefined}
          />
        </div>
        <button
          type="submit"
          className="btn-danger"
          disabled={reset.isPending}
          aria-busy={reset.isPending}
        >
          {reset.isPending ? 'Resetando…' : 'Resetar cliente'}
        </button>
      </form>

      {error && (
        <div id="reset-error" role="alert" className="card border-sara-danger">
          <p className="text-sara-danger text-sm">{error}</p>
        </div>
      )}

      {lastResult && (
        <div className="card animate-fade-in" role="status" aria-live="polite">
          {lastResult.clienteId ? (
            <>
              <p className="text-sara-success font-medium mb-2">✓ Cliente resetado</p>
              <p className="text-sm muted mb-3">ID: {lastResult.clienteId}</p>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {(Object.entries(lastResult.apagados) as Array<[string, number]>).map(([k, v]) => (
                  <div
                    key={k}
                    className="flex flex-col rounded-lg bg-sara-bg border border-sara-border p-3"
                  >
                    <dt className="text-xs muted capitalize">{k}</dt>
                    <dd className="text-lg font-semibold text-sara-text mt-0.5">{v}</dd>
                  </div>
                ))}
              </dl>
            </>
          ) : (
            <p className="muted text-sm">
              Nenhum cliente encontrado com esse telefone — nada a apagar (idempotente).
            </p>
          )}
        </div>
      )}

      <details className="card text-sm muted">
        <summary className="cursor-pointer font-medium text-sara-text">
          Como testar fim-a-fim
        </summary>
        <ol className="mt-3 space-y-1.5 list-decimal list-inside">
          <li>Reseta o número aqui.</li>
          <li>Manda "oi" pra Sara pelo WhatsApp.</li>
          <li>
            Veja a conversa em{' '}
            <code className="px-1 rounded bg-sara-surface-hover">/conversas</code> — deve aparecer
            pausada=false, intervenção=false.
          </li>
          <li>Continua a conversa testando o intent que quiser (carta_dia, tiragem, etc).</li>
          <li>Quando quiser começar do zero, volta aqui e reseta.</li>
          <li>
            Pra testar risco emocional: manda "vou me matar" — vai pausar Sara e abrir ticket
            crítico.
          </li>
        </ol>
      </details>
    </article>
  );
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return digits;
}
